import { SixOrbitInvoicePushDialog } from './SixOrbitInvoicePushDialog';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import PublishIcon from '@mui/icons-material/Publish';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import {
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { formatUIDate,  formatBoxPieces, toDateInput } from '@tiles-erp/shared';
import { usePagination } from '@tiles-erp/hooks';
import { DateField,  ConfirmDialog, PageContainer } from '@tiles-erp/ui';
import type { SalesInvoiceItem, SalesInvoiceStatus } from '@tiles-erp/shared-types';
import { DataTable } from '../../components/DataTable';
import { fetchAllListRows, ListExportButtons, type ExportColumn } from '../../components/ListExportButtons';
import { ApiError } from '../../lib/api-client';
import { useBranches } from '../products/branch-prices-api';
import { useAuth } from '../../auth/AuthProvider';
import { PERMISSIONS } from '@tiles-erp/config';
import { SyncStatusChip } from '../sixorbit/SyncStatusChip';
import { useCustomers } from './api';
import {
  useCancelSalesInvoice,
  useDeleteSalesInvoice,
  usePostSalesInvoice,
  useSalesInvoice,
  useSalesInvoices,
} from './invoices-api';
import { useSessionBranchId } from '../../lib/session-branch';

const STATUS_COLORS: Record<SalesInvoiceStatus, 'default' | 'success' | 'error'> = {
  DRAFT: 'default',
  POSTED: 'success',
  CANCELLED: 'error',
};

const STATUSES: SalesInvoiceStatus[] = ['DRAFT', 'POSTED', 'CANCELLED'];
const DATE_RANGE_SESSION_KEY = 'tiles-erp:sales-invoices:date-range';

const initialInvoiceDateRange = (): { fromDate: string; toDate: string } => {
  const today = toDateInput(new Date());
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(DATE_RANGE_SESSION_KEY) ?? '{}') as {
      fromDate?: unknown;
      toDate?: unknown;
    };
    const valid = (value: unknown): value is string =>
      typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
    return {
      fromDate: valid(saved.fromDate) ? saved.fromDate : today,
      toDate: valid(saved.toDate) ? saved.toDate : today,
    };
  } catch {
    return { fromDate: today, toDate: today };
  }
};

/** Invoice documents available from the normal row print action. */
const PRINT_OPTIONS = [
  { document: 'tax', paper: 'A4', label: 'Tax invoice — A4' },
  { document: 'tax', paper: 'A5', label: 'Tax invoice — A5' },
  { document: 'items', paper: 'A4', label: 'Item list — A4' },
  { document: 'items', paper: 'A5', label: 'Item list — A5' },
  { document: 'proforma', paper: 'A4', label: 'Proforma invoice — A4' },
  { document: 'proforma', paper: 'A5', label: 'Proforma invoice — A5' },
  { document: 'tax', paper: '80mm', label: 'Tax invoice — 80 mm roll' },
  { document: 'tax', paper: '58mm', label: 'Tax invoice — 58 mm roll' },
] as const;

const money = (value: number): string =>
  `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const salesInvoiceExportColumns: ExportColumn<SalesInvoiceItem>[] = [
  { header: 'Invoice no', value: (row) => row.invoiceNumber, width: 125 },
  { header: 'Date', value: (row) => formatUIDate(new Date(row.invoiceDate)), width: 80 },
  { header: 'Customer', value: (row) => row.customerName, width: 180 },
  { header: 'Phone', value: (row) => row.customerMobile ?? '', width: 100 },
  { header: 'Salesman', value: (row) => row.salesmanName ?? '', width: 120 },
  { header: 'Billed by', value: (row) => row.billedByName ?? '', width: 120 },
  { header: 'Total', value: (row) => row.grandTotal, width: 90 },
  { header: 'Balance', value: (row) => row.balanceAmount, width: 90 },
  { header: 'Status', value: (row) => row.status, width: 90 },
  { header: 'SixOrbit', value: (row) => row.sixorbitSyncStatus, width: 100 },
];

/**
 * Sales invoices: the document that takes stock out and puts the value on the customer's
 * account. Drafts can be edited or deleted; posted invoices can only be cancelled.
 */
export function SalesInvoicesPage(): JSX.Element {
  const navigate = useNavigate();
  const pagination = usePagination();
  const customers = useCustomers();
  const branches = useBranches();
  const { user, hasPermission } = useAuth();
  const canSyncSixOrbit = hasPermission(PERMISSIONS.SIXORBIT_SYNC);
  const canPrintOriginalDeliverySlip = Boolean(user?.roles.includes('DELIVERY SLIP PRINT'));
  const canChangeBranch = Boolean(user?.roles.some((role) => role === 'ADMIN' || role === 'SUPER_ADMIN'));
  const availableBranches = (branches.data ?? []).filter((branch) => canChangeBranch || user?.branchIds.includes(branch.id));
  const [customerId, setCustomerId] = useState('');
  const [branchId, setBranchId] = useSessionBranchId();
  const [status, setStatus] = useState<SalesInvoiceStatus | ''>('');
  const [{ fromDate, toDate }, setDateRange] = useState(initialInvoiceDateRange);
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    ...initialInvoiceDateRange(),
    customerId: '',
    branchId,
    status: '' as SalesInvoiceStatus | '',
  }));
  useEffect(() => {
    if (!canChangeBranch && user?.branchIds.length) {
      const allowedBranch = user.branchIds.includes(branchId) ? branchId : user.branchIds[0]!;
      setBranchId(allowedBranch);
      setAppliedFilters((current) => ({ ...current, branchId: allowedBranch }));
    }
  }, [branchId, canChangeBranch, setBranchId, user?.branchIds]);
  useEffect(() => {
    try {
      window.sessionStorage.setItem(DATE_RANGE_SESSION_KEY, JSON.stringify({
        fromDate: appliedFilters.fromDate,
        toDate: appliedFilters.toDate,
      }));
    } catch {
      // Storage can be disabled; the filters still work until the page is refreshed.
    }
  }, [appliedFilters.fromDate, appliedFilters.toDate]);

  const { data, isFetching } = useSalesInvoices(pagination.query, {
    fromDate: appliedFilters.fromDate,
    toDate: appliedFilters.toDate,
    customerId: appliedFilters.customerId || undefined,
    branchId: appliedFilters.branchId || undefined,
    status: appliedFilters.status || undefined,
  });
  const exportParams = {
    search: pagination.query.search,
    customerId: appliedFilters.customerId || undefined,
    branchId: appliedFilters.branchId || undefined,
    status: appliedFilters.status || undefined,
    fromDate: appliedFilters.fromDate || undefined,
    toDate: appliedFilters.toDate || undefined,
  };
  const allInvoices = useQuery({
    queryKey: ['sales-invoices', 'totals', exportParams],
    queryFn: () => fetchAllListRows<SalesInvoiceItem>('/sales-invoices', exportParams),
  });
  const invoiceTotals = useMemo(() => (allInvoices.data ?? [])
    .filter((invoice) => invoice.status !== 'CANCELLED')
    .reduce((total, invoice) => ({
      amount: total.amount + invoice.grandTotal,
      balance: total.balance + invoice.balanceAmount,
    }), { amount: 0, balance: 0 }), [allInvoices.data]);

  const postInvoice = usePostSalesInvoice();
  const cancelInvoice = useCancelSalesInvoice();
  const deleteInvoice = useDeleteSalesInvoice();
  const [pushingInvoice,setPushingInvoice] = useState<SalesInvoiceItem|null>(null);

  const [viewingId, setViewingId] = useState<string | null>(null);
  const [printMenu, setPrintMenu] = useState<{
    anchor: HTMLElement;
    id: string;
  } | null>(null);
  const [cancelling, setCancelling] = useState<SalesInvoiceItem | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [deleting, setDeleting] = useState<SalesInvoiceItem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const detail = useSalesInvoice(viewingId);

  const run = async (action: () => Promise<unknown>, message: string): Promise<void> => {
    setError(null);
    try {
      await action();
      setNotice(message);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong');
    }
  };

  const submitCancel = (): void => {
    if (!cancelling) return;
    const invoice = cancelling;
    void run(async () => {
      await cancelInvoice.mutateAsync({
        id: invoice.id,
        version: invoice.version,
        reason: cancelReason,
      });
      setCancelling(null);
      setCancelReason('');
    }, `${invoice.invoiceNumber} cancelled and the stock returned.`);
  };

  const columns = useMemo<ColDef<SalesInvoiceItem>[]>(
    () => [
      { field: 'invoiceNumber', headerName: 'Invoice no', minWidth: 150 },
      {
        field: 'invoiceDate',
        headerName: 'Date',
        minWidth: 115,
        valueFormatter: (p) => (p.value ? formatUIDate(new Date(p.value as string)) : ''),
      },
      { field: 'customerName', headerName: 'Customer', minWidth: 190 },
      { field: 'customerMobile', headerName: 'Phone', minWidth: 130 },
      { field: 'salesmanName', headerName: 'Salesman', minWidth: 150 },
      { field: 'billedByName', headerName: 'Billed by', minWidth: 150 },
      {
        field: 'grandTotal',
        headerName: 'Total',
        maxWidth: 150,
        cellStyle: { fontWeight: 600 },
        valueFormatter: (p) => money(p.value as number),
      },
      {
        field: 'balanceAmount',
        headerName: 'Balance',
        maxWidth: 140,
        valueFormatter: (p) => money(p.value as number),
      },
      {
        field: 'status',
        headerName: 'Status',
        maxWidth: 130,
        cellRenderer: (p: ICellRendererParams<SalesInvoiceItem>) => (
          <Chip
            label={p.value as string}
            size="small"
            color={STATUS_COLORS[p.value as SalesInvoiceStatus]}
          />
        ),
      },
      {
        field: 'sixorbitSyncStatus',
        headerName: 'SixOrbit',
        minWidth: 125,
        cellRenderer: (p: ICellRendererParams<SalesInvoiceItem>) => (
          <SyncStatusChip status={p.data?.sixorbitSyncStatus ?? null} error={p.data?.sixorbitSyncError} />
        ),
      },
      {
        headerName: '',
        minWidth: 250,
        cellRenderer: (p: ICellRendererParams<SalesInvoiceItem>) => {
          const invoice = p.data;
          if (!invoice) return null;
          const isDraft = invoice.status === 'DRAFT';
          return (
            <>
              {canSyncSixOrbit && (
                <Tooltip title={invoice.sixorbitId ? `SixOrbit ${invoice.sixorbitOrderId ?? invoice.sixorbitId}` : 'Push invoice as SixOrbit sales order'}>
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={Boolean(invoice.sixorbitId) || invoice.status !== 'POSTED'}
                      onClick={() => {setError(null);setPushingInvoice(invoice);}}
                    >
                      <CloudUploadIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="View lines">
                <IconButton size="small" onClick={() => setViewingId(invoice.id)}>
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={invoice.status === 'POSTED' ? 'Print invoice documents' : 'Post the invoice before printing'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={invoice.status !== 'POSTED'}
                    onClick={(event) => setPrintMenu({
                      anchor: event.currentTarget,
                      id: invoice.id,
                    })}
                  >
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {canPrintOriginalDeliverySlip && !invoice.originalDeliverySlipPrinted && (
                <Tooltip title={invoice.status === 'POSTED' ? 'Print original delivery slip' : 'Post the invoice before printing'}>
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={invoice.status !== 'POSTED'}
                      onClick={() => navigate(`/sales-invoices/${invoice.id}/print?document=delivery&paper=80mm`)}
                    >
                      <LocalShippingIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={isDraft ? 'Post: takes the stock out' : 'Already posted'}>
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    disabled={!isDraft}
                    onClick={() =>
                      void run(
                        () =>
                          postInvoice.mutateAsync({ id: invoice.id, version: invoice.version }),
                        `${invoice.invoiceNumber} posted; stock issued.`,
                      )
                    }
                  >
                    <PublishIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  invoice.returnedAmount > 0
                    ? 'Invoices with sales returns cannot be cancelled'
                    : 'Cancel and return the stock'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    color="warning"
                    disabled={invoice.status === 'CANCELLED' || invoice.returnedAmount > 0}
                    onClick={() => {
                      setCancelling(invoice);
                      setCancelReason('');
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isDraft ? 'Delete draft' : 'Only drafts can be deleted'}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={!isDraft}
                    onClick={() => setDeleting(invoice)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          );
        },
      },
    ],
    [canSyncSixOrbit],
  );

  return (
    <PageContainer
      title="Sales invoices"
      subtitle="Raise invoices from the Sales orders page; posting one issues the stock."
    >
      <Stack spacing={1}>
        {notice && (
          <Alert severity="success" onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ whiteSpace: 'pre-line' }}>
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <DateField
            label="From date"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => {
              setDateRange((current) => ({ ...current, fromDate: e.target.value }));
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: toDate || undefined }}
            sx={{ width: { xs: '100%', sm: 165 } }}
          />
          <DateField
            label="To date"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => {
              setDateRange((current) => ({ ...current, toDate: e.target.value }));
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: fromDate || undefined }}
            sx={{ width: { xs: '100%', sm: 165 } }}
          />
          <TextField
            select
            label="Customer"
            size="small"
            fullWidth={false}
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
            }}
            sx={{ width: 200 }}
          >
            <MenuItem value="">All customers</MenuItem>
            {(customers.data ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Branch"
            size="small"
            fullWidth={false}
            value={branchId}
            disabled={!canChangeBranch && availableBranches.length === 0}
            onChange={(e) => {
              setBranchId(e.target.value);
            }}
            sx={{ width: 180 }}
          >
            {canChangeBranch && <MenuItem value="">All branches</MenuItem>}
            {availableBranches.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            size="small"
            fullWidth={false}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as SalesInvoiceStatus | '');
            }}
            sx={{ width: 160 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            startIcon={<FilterAltIcon />}
            onClick={() => {
              setAppliedFilters({ fromDate, toDate, customerId, branchId, status });
              pagination.setPage(1);
            }}
          >
            Filter
          </Button>
          <ListExportButtons<SalesInvoiceItem>
            path="/sales-invoices"
            params={exportParams}
            columns={salesInvoiceExportColumns}
            title="Sales Invoices"
            filename="sales-invoices"
            onError={setError}
            footerRows={(rows) => {
              const totals = rows.filter((row) => row.status !== 'CANCELLED').reduce((sum, row) => ({ amount: sum.amount + row.grandTotal, balance: sum.balance + row.balanceAmount }), { amount: 0, balance: 0 });
              return [['TOTAL (excluding cancelled)', '', '', '', '', '', totals.amount, totals.balance, '', '']];
            }}
          />
        </Stack>

        <DataTable
          exportable={false}
          rows={data?.items ?? []}
          columns={columns}
          meta={data?.meta}
          pagination={pagination}
          loading={isFetching}
          searchPlaceholder="Search invoice, customer, phone or salesman…"
          height={580}
          gridOptions={{ enableCellTextSelection: true, ensureDomOrder: true }}
        />
        <Stack direction="row" spacing={3} justifyContent="flex-end" flexWrap="wrap" useFlexGap sx={{ px: 1, py: 0.75 }}>
          <Typography variant="body2" fontWeight={700}>Invoice total: {money(invoiceTotals.amount)}</Typography>
          <Typography variant="body2" fontWeight={700}>Balance total: {money(invoiceTotals.balance)}</Typography>
          <Typography variant="caption" color="text.secondary">Cancelled invoices excluded</Typography>
        </Stack>
      </Stack>

      <Menu
        open={printMenu !== null}
        anchorEl={printMenu?.anchor ?? null}
        onClose={() => setPrintMenu(null)}
      >
        {PRINT_OPTIONS.map((option) => (
          <MenuItem
            key={`${option.document}-${option.paper}`}
            onClick={() => {
              const target = printMenu;
              setPrintMenu(null);
              if (target) navigate(`/sales-invoices/${target.id}/print?document=${option.document}&paper=${option.paper}`);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      <SixOrbitInvoicePushDialog invoice={pushingInvoice} onClose={()=>setPushingInvoice(null)} onPushed={setNotice} />
      <Dialog open={viewingId !== null} onClose={() => setViewingId(null)} maxWidth="md" fullWidth>
        <DialogTitle>{detail.data?.invoiceNumber ?? 'Sales invoice'}</DialogTitle>
        <DialogContent>
          {detail.data && (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                {detail.data.customerName} · {detail.data.branchName} ·{' '}
                {formatUIDate(new Date(detail.data.invoiceDate))}
                {detail.data.orderNumber ? ` · from ${detail.data.orderNumber}` : ''}
                {detail.data.customerGstin ? ` · GSTIN ${detail.data.customerGstin}` : ''}
              </Typography>
              {detail.data.cancelReason && (
                <Alert severity="warning">Cancelled: {detail.data.cancelReason}</Alert>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Godown</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">GST %</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detail.data.lines ?? []).map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>
                        {line.productName}
                        {line.batchNo && (
                          <Typography variant="caption" color="text.secondary">
                            {' '}
                            · {line.batchNo}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{line.godownName}</TableCell>
                      <TableCell align="right">
                        {formatBoxPieces(
                          line.qtyBoxes,
                          line.piecesPerBox,
                          line.baseUom === 'PIECE',
                        )}
                      </TableCell>
                      <TableCell align="right">{money(line.rate)}</TableCell>
                      <TableCell align="right">{line.gstRate}</TableCell>
                      <TableCell align="right">{money(line.lineTotal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Stack direction="row" justifyContent="flex-end" spacing={3}>
                <Typography variant="body2">Sub total {money(detail.data.subTotal)}</Typography>
                {detail.data.isInterState ? (
                  <Typography variant="body2">IGST {money(detail.data.igstAmount)}</Typography>
                ) : (
                  <>
                    <Typography variant="body2">CGST {money(detail.data.cgstAmount)}</Typography>
                    <Typography variant="body2">SGST {money(detail.data.sgstAmount)}</Typography>
                  </>
                )}
                <Typography variant="subtitle2">
                  Total {money(detail.data.grandTotal)}
                </Typography>
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={cancelling !== null}
        onClose={() => setCancelling(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel {cancelling?.invoiceNumber}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              A posted invoice puts its stock back into the godown it left from, and the order is
              re-opened for that quantity.
            </Typography>
            <TextField
              label="Reason *"
              size="small"
              multiline
              minRows={2}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setCancelling(null)}>
            Keep invoice
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={submitCancel}
            disabled={cancelReason.trim().length < 3 || cancelInvoice.isPending}
          >
            Cancel invoice
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete draft invoice"
        message={`Delete ${deleting?.invoiceNumber}? Posted invoices must be cancelled instead.`}
        confirmLabel="Delete"
        destructive
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          const invoice = deleting;
          setDeleting(null);
          if (invoice) {
            void run(
              () => deleteInvoice.mutateAsync(invoice.id),
              `${invoice.invoiceNumber} deleted.`,
            );
          }
        }}
      />
    </PageContainer>
  );
}
